import { type PropsWithChildren, useMemo, useState } from 'react'

import { createStyles } from 'antd-style'

import { localdb } from '@eastcoal/common/utils/store'
import { unicode2HtmlEntity } from '@eastcoal/common/utils/unicode'

import data from './emoji.json'
import type { EmojiDropdownProps, EmojiObject } from './interface'

const useStyles = createStyles(({ prefixCls, token }) => ({
  panel: {
    position: 'relative',
    height: 374,
    padding: `${token.paddingSM}px ${token.padding}px`,
    margin: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    listStyle: 'none',
    backgroundColor: token.colorWhite,
    borderRadius: token.borderRadiusLG
  },
  group: {
    [`&+&`]: {
      marginBlockStart: token.margin
    }
  },
  groupTitle: {
    marginBlockEnd: token.marginXXS,
    fontSize: token.fontSize,
    color: token.colorTextLabel
  },
  itemContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(10, 32px)',
    textAlign: 'center',
    lineHeight: '32px'
  },
  item: {
    fontSize: token.fontSizeXL,
    borderRadius: token.borderRadius,
    [`&:hover`]: {
      backgroundColor: token.colorBgTextHover
    }
  }
}))

const LOCAL_KEY = 'emojis'

const EXPIRE_SECONDS = 30 * 24 * 60 * 60

const localEmojis: EmojiObject[] = localdb.get(LOCAL_KEY) ?? []

export const EmojiPanel = ({
  onSelect
}: PropsWithChildren<EmojiDropdownProps>) => {
  const [selectedEmojis, setSelectedEmojis] = useState(localEmojis)
  const handleEmojiClick = (value: EmojiObject) => {
    onSelect?.(value)
    const filteredEmojis = selectedEmojis.filter(
      item => item.code !== value.code
    )
    filteredEmojis.unshift(value)
    const newSelectedEmojis = filteredEmojis.slice(0, 20)
    setSelectedEmojis(newSelectedEmojis)
    localdb.set(LOCAL_KEY, newSelectedEmojis, EXPIRE_SECONDS)
  }

  const mergedData = useMemo(() => {
    return selectedEmojis.length
      ? [
          {
            type: 'selected',
            title: '最近使用',
            info: selectedEmojis
          },
          ...data
        ]
      : data
  }, [selectedEmojis])

  const { styles } = useStyles()

  return (
    <ul className={styles.panel}>
      {mergedData.map(({ type, title, info }) => (
        <li key={type} className={styles.group}>
          <div className={styles.groupTitle}>{title}</div>
          <div className={styles.itemContainer}>
            {info.map(item => (
              <span
                key={item.code}
                dangerouslySetInnerHTML={{
                  __html: unicode2HtmlEntity(item.code)
                }}
                aria-label={item.code}
                className={styles.item}
                role='button'
                tabIndex={-1}
                onClick={() => handleEmojiClick(item)}
              />
            ))}
          </div>
        </li>
      ))}
    </ul>
  )
}
